import { useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { accountStatementsApi, type AccountStatementSummaryDto, type IssueStatementRequest } from "@/lib/clients-accounts";
import { ListPage } from "@/archetypes/ListPage";
import { Button } from "@/components/ui/button";
import { DetailsDrawer } from "@/components/details-drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

export function AccountStatementsListPage() {
  const { id = "0" } = useParams();
  const accountId = Number(id);
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [forDate, setForDate] = useState(new Date().toISOString().slice(0, 10));
  const [issueMethod, setIssueMethod] = useState<"Email" | "Print" | "Manual">("Email");

  const { data, isLoading } = useQuery({
    queryKey: ["account-statements", accountId],
    queryFn: () => accountStatementsApi.list(accountId),
  });

  const issueMut = useMutation({
    mutationFn: (req: IssueStatementRequest) => accountStatementsApi.issue(accountId, req),
    onSuccess: () => {
      toast.success("Statement enqueued");
      setDrawerOpen(false);
      void qc.invalidateQueries({ queryKey: ["account-statements", accountId] });
    },
    onError: () => toast.error("Failed to enqueue statement"),
  });

  return (
    <ListPage
      title="Statements"
      backTo={`/accounts/${accountId}`}
      primaryAction={<Button onClick={() => setDrawerOpen(true)}>Generate new statement</Button>}
    >
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted-foreground">No statements issued yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="px-2 py-1">Statement #</th><th className="px-2 py-1">Date</th>
              <th className="px-2 py-1">Period</th><th className="px-2 py-1 text-right">Opening</th>
              <th className="px-2 py-1 text-right">Closing</th><th className="px-2 py-1">Issued</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data!.items.map((s: AccountStatementSummaryDto) => (
              <tr key={s.statementId} className="border-b">
                <td className="px-2 py-1">{s.statementNo}</td>
                <td className="px-2 py-1">{new Date(s.statementDate).toLocaleDateString("en-AU")}</td>
                <td className="px-2 py-1">{new Date(s.periodFromDate).toLocaleDateString("en-AU")} — {new Date(s.periodToDate).toLocaleDateString("en-AU")}</td>
                <td className="px-2 py-1 text-right">{fmt.format(s.openingBalance)}</td>
                <td className="px-2 py-1 text-right">{fmt.format(s.closingBalance)}</td>
                <td className="px-2 py-1">{s.issueMethod ?? "—"}</td>
                <td className="px-2 py-1">
                  <Link to={`/accounts/${accountId}/statements/${s.statementId}`} className="underline">Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <DetailsDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title="Generate new statement">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            issueMut.mutate({ forDate: new Date(forDate).toISOString(), outstandingOverride: null, issueMethod });
          }}
        >
          <div>
            <Label htmlFor="for-date">For date</Label>
            <Input id="for-date" type="date" value={forDate} onChange={(e) => setForDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="method">Issue method</Label>
            <Select value={issueMethod} onValueChange={(v) => setIssueMethod(v as typeof issueMethod)}>
              <SelectTrigger id="method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Print">Print</SelectItem>
                <SelectItem value="Manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={issueMut.isPending}>
            {issueMut.isPending ? "Enqueuing…" : "Generate"}
          </Button>
        </form>
      </DetailsDrawer>
    </ListPage>
  );
}
